import ClientReportInfo from './components/biReports/ClientReportInfo';
import React from 'react';
import { reportToggleSx } from './components/biReports/clientReportStyles';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import Client from './components/Client';
import BackofficeReportsApi from './components/biReports/backofficeReportsApi';
import ClientMetricCard from './components/biReports/ClientMetricCard';
import ClientsDistributionSection from './components/biReports/ClientsDistributionSection';
import ClientsEvolutionChart from './components/biReports/ClientsEvolutionChart';
import ClientsRecentRegistrations from './components/biReports/ClientsRecentRegistrations';
import ClientsActivationOverview from './components/biReports/ClientsActivationOverview';
import { reportErrorMessage } from './components/biReports/clientPurchaseReportUtils';
import RapClientsActivation from './RapClientsActivation';
import CustomPeriodPopover, { businessTodayIso, customPeriodDays, validateCustomPeriod } from './components/biReports/CustomPeriodPopover';

const numberFormatter = new Intl.NumberFormat('ro-RO', {
  maximumFractionDigits: 0,
});

export function formatTotalClientsDetail(total, periodMode) {
  const registrationPeriod = periodMode === 'today'
    ? 'registrations today'
    : 'registrations in selected period';
  const growth = total?.growth_pct === null || typeof total?.growth_pct === 'undefined'
    ? ''
    : ` · ${Number(total.growth_pct).toFixed(1)}% growth`;
  const totalLabel = periodMode === 'custom' ? 'Total at period end' : 'Current total';
  return `${totalLabel} · +${numberFormatter.format(Number(total?.new_in_period || 0))} ${registrationPeriod}${growth}`;
}

function formatPeriodRange(period) {
  if (!period?.dateFrom || !period?.dateTo) return '';
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  const from = new Date(`${period.dateFrom}T12:00:00`).toLocaleDateString('ro-RO', options);
  const to = new Date(`${period.dateTo}T12:00:00`).toLocaleDateString('ro-RO', options);
  return `${from} – ${to}`;
}

function comparisonDetail(metric) {
  if (!metric || metric.change_pct === null) return '';
  return 'vs prior period';
}

function formatUpdateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleTimeString('ro-RO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Bucharest',
  });
}

function latestUpdate(values) {
  return values.reduce((latest, value) => {
    if (!value || Number.isNaN(new Date(value).getTime())) return latest;
    if (!latest || new Date(value).getTime() > new Date(latest).getTime()) {
      return value;
    }
    return latest;
  }, null);
}

class RapClients extends React.Component {
  constructor(props) {
    super(props);

    this.client = new Client('none');
    this.reportRef = React.createRef();
    this.customPeriodButton = React.createRef();
    this.isMountedFlag = false;
    this.initialLoadTimer = null;
    this.requests = {};
    this.requestIds = {
      evolution: 0,
      distributions: 0,
      recent: 0,
      export: 0,
    };
    this.distributionCache = {};
    this.distributionCacheUpdatedAt = {};
    this.openEvolutionExportMenu = null;

    this.state = {
      reportView: 'overview',
      periodMode: 'last_30d',
      dateFrom: '',
      dateTo: '',
      customPeriodOpen: false,
      draftDateFrom: '',
      draftDateTo: '',
      customPeriodError: '',
      countyFilter: 'all',
      distributionScope: 'all',
      countyOptions: [{ value: 'all', label: 'All counties' }],
      evolution: null,
      distributions: null,
      recentClients: null,
      evolutionStatus: 'idle',
      distributionsStatus: 'idle',
      recentClientsStatus: 'idle',
      evolutionUpdatedAt: null,
      distributionsUpdatedAt: null,
      recentUpdatedAt: null,
      exportStatus: 'idle',
    };
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.reportTopBar = document.querySelector('.topBar');
    this.updateReportTopOffset();
    if (this.reportTopBar && typeof ResizeObserver !== 'undefined') {
      this.reportTopBarObserver = new ResizeObserver(this.updateReportTopOffset);
      this.reportTopBarObserver.observe(this.reportTopBar);
    }
    window.addEventListener('resize', this.updateReportTopOffset);

    // React 18 StrictMode immediately mounts, unmounts and remounts components
    // in development. Defer the initial requests so the probe mount does not
    // create three requests that are immediately aborted by its cleanup.
    this.initialLoadTimer = window.setTimeout(() => {
      this.initialLoadTimer = null;
      if (!this.isMountedFlag) return;

      this.loadEvolution();
      this.loadDistributions();
      this.loadRecentClients();
    }, 0);

    if (this.props.onReady) this.props.onReady();
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    this.reportTopBarObserver?.disconnect();
    window.removeEventListener('resize', this.updateReportTopOffset);
    if (this.initialLoadTimer !== null) {
      window.clearTimeout(this.initialLoadTimer);
      this.initialLoadTimer = null;
    }
    Object.values(this.requests).forEach((request) => {
      if (request && typeof request.abort === 'function') request.abort();
    });
  }

  startRequest = (section, request, onSuccess) => {
    const requestId = this.requestIds[section] + 1;
    this.requestIds[section] = requestId;

    const previousRequest = this.requests[section];
    if (previousRequest && typeof previousRequest.abort === 'function') {
      previousRequest.abort();
    }
    this.requests[section] = request;
    this.setState({ [`${section}Status`]: 'loading', [`${section}ErrorMessage`]: null });

    const handleSuccess = (data) => {
      if (!this.isMountedFlag || this.requestIds[section] !== requestId) return;
      this.requests[section] = null;
      const updatedAt = data?.generated_at || new Date().toISOString();
      onSuccess(data, updatedAt);
      this.setState({
        [`${section}Status`]: 'success',
        [`${section}UpdatedAt`]: updatedAt,
      });
    };

    const handleFailure = (xhr, textStatus) => {
      if (textStatus === 'abort') return;
      if (!this.isMountedFlag || this.requestIds[section] !== requestId) return;
      this.requests[section] = null;
      this.setState({ [`${section}Status`]: 'error', [`${section}ErrorMessage`]: reportErrorMessage(xhr) });
    };

    if (request && typeof request.done === 'function') {
      request.done(handleSuccess);
      request.fail(handleFailure);
    } else {
      Promise.resolve(request).then(handleSuccess).catch(handleFailure);
    }
  };

  loadEvolution = () => {
    const request = BackofficeReportsApi.getClientsEvolutionAsync(
      this.client,
      {
        ...this.periodQuery(),
        county: this.state.countyFilter,
      },
    );
    this.startRequest('evolution', request, (data) => {
      const countyOptions = Array.isArray(data?.county_options)
        && data.county_options.length
        ? data.county_options
        : this.state.countyOptions;
      this.setState({ evolution: data || null, countyOptions });
    });
  };

  periodQuery = () => ({
    periodMode: this.state.periodMode,
    ...(this.state.periodMode === 'custom'
      ? { dateFrom: this.state.dateFrom, dateTo: this.state.dateTo }
      : {}),
  });

  distributionCacheKey = (
    scope, periodMode, county = this.state.countyFilter,
    dateFrom = this.state.dateFrom, dateTo = this.state.dateTo,
  ) => (
    scope === 'all' ? `all:${county}` : `${scope}:${periodMode}:${county}${periodMode === 'custom' ? `:${dateFrom}:${dateTo}` : ''}`
  );

  cancelRequest = (section) => {
    this.requestIds[section] += 1;
    const request = this.requests[section];
    if (request && typeof request.abort === 'function') request.abort();
    this.requests[section] = null;
  };

  loadDistributions = ({ force = false } = {}) => {
    const { distributionScope, periodMode } = this.state;
    const cacheKey = this.distributionCacheKey(
      distributionScope,
      periodMode,
      this.state.countyFilter,
    );
    if (!force && this.distributionCache[cacheKey]) {
      this.cancelRequest('distributions');
      this.setState({
        distributions: this.distributionCache[cacheKey],
        distributionsStatus: 'success',
        distributionsUpdatedAt: this.distributionCacheUpdatedAt[cacheKey] || null,
      });
      return;
    }

    const request = BackofficeReportsApi.getClientsDistributionsAsync(
      this.client,
      {
        scope: distributionScope,
        ...this.periodQuery(),
        county: this.state.countyFilter,
      },
    );
    this.startRequest('distributions', request, (data, updatedAt) => {
      this.distributionCache[cacheKey] = data;
      this.distributionCacheUpdatedAt[cacheKey] = updatedAt;
      this.setState({ distributions: data || null });
    });
  };

  loadRecentClients = () => {
    const request = BackofficeReportsApi.getClientsListAsync(this.client, {});
    this.startRequest('recent', request, (data) => {
      this.setState({ recentClients: data?.clients || [] });
    });
  };

  loadEvolutionForExport = (periodSelection) => {
    const exportQuery = typeof periodSelection === 'string'
      ? { periodMode: periodSelection }
      : { ...periodSelection };
    const exportPeriodMode = exportQuery.periodMode;
    if (
      exportPeriodMode === this.state.periodMode
      && !exportQuery.dateFrom
      && !exportQuery.dateTo
      && this.state.evolution
      && this.state.evolution?.filters?.county?.value === this.state.countyFilter
    ) {
      return Promise.resolve(this.state.evolution);
    }

    const requestId = this.requestIds.export + 1;
    this.requestIds.export = requestId;
    const previousRequest = this.requests.export;
    if (previousRequest && typeof previousRequest.abort === 'function') {
      previousRequest.abort();
    }

    const request = BackofficeReportsApi.getClientsEvolutionAsync(
      this.client,
      { ...exportQuery, county: this.state.countyFilter },
    );
    this.requests.export = request;
    this.setState({ exportStatus: 'loading' });

    return new Promise((resolve, reject) => {
      const handleSuccess = (data) => {
        if (!this.isMountedFlag || this.requestIds.export !== requestId) return;
        this.requests.export = null;
        this.setState({ exportStatus: 'success' });
        resolve(data);
      };
      const handleFailure = (xhr, textStatus) => {
        if (!this.isMountedFlag || this.requestIds.export !== requestId) return;
        this.requests.export = null;
        if (textStatus === 'abort') {
          reject({ aborted: true });
          return;
        }
        this.setState({ exportStatus: 'error' });
        reject(new Error('Client evolution export could not be loaded.'));
      };

      if (request && typeof request.done === 'function') {
        request.done(handleSuccess);
        request.fail(handleFailure);
      } else {
        Promise.resolve(request).then(handleSuccess).catch(handleFailure);
      }
    });
  };

  handleRefreshAll = () => {
    this.loadEvolution();
    this.loadDistributions({ force: true });
    this.loadRecentClients();
  };

  handleEvolutionExportMenuReady = (openMenu) => {
    this.openEvolutionExportMenu = openMenu;
  };

  handleEvolutionExportClick = (event) => {
    if (this.openEvolutionExportMenu) {
      this.openEvolutionExportMenu(event.currentTarget);
    }
  };

  handlePeriodChange = (event, newPeriodMode) => {
    if (newPeriodMode === 'custom') {
      this.setState({
        customPeriodOpen: true,
        draftDateFrom: this.state.dateFrom || this.state.evolution?.period?.dateFrom || businessTodayIso(),
        draftDateTo: this.state.dateTo || this.state.evolution?.period?.dateTo || businessTodayIso(),
        customPeriodError: '',
      });
      return;
    }
    if (!newPeriodMode || newPeriodMode === this.state.periodMode) return;
    this.applyPeriod(newPeriodMode);
  };

  handleCustomPeriodApply = () => {
    const { draftDateFrom, draftDateTo } = this.state;
    const customPeriodError = validateCustomPeriod(draftDateFrom, draftDateTo);
    if (customPeriodError) {
      this.setState({ customPeriodError });
      return;
    }
    this.applyPeriod('custom', draftDateFrom, draftDateTo);
  };

  applyPeriod = (newPeriodMode, dateFrom = this.state.dateFrom, dateTo = this.state.dateTo) => {
    const cachedDistributions = this.state.distributionScope === 'new'
      ? this.distributionCache[this.distributionCacheKey(
        'new',
        newPeriodMode,
        this.state.countyFilter,
        dateFrom,
        dateTo,
      )] || null
      : this.state.distributions;
    const cachedUpdatedAt = this.state.distributionScope === 'new'
      ? this.distributionCacheUpdatedAt[this.distributionCacheKey(
        'new',
        newPeriodMode,
        this.state.countyFilter,
        dateFrom,
        dateTo,
      )] || null
      : this.state.distributionsUpdatedAt;
    this.setState({
      periodMode: newPeriodMode,
      dateFrom,
      dateTo,
      customPeriodOpen: false,
      evolution: null,
      evolutionUpdatedAt: null,
      distributions: cachedDistributions,
      distributionsUpdatedAt: cachedUpdatedAt,
    }, () => {
      this.loadEvolution();
      if (this.state.distributionScope === 'new') this.loadDistributions();
    });
  };

  handleScopeChange = (event, newScope) => {
    if (!newScope || newScope === this.state.distributionScope) return;
    const cachedDistributions = this.distributionCache[
      this.distributionCacheKey(
        newScope,
        this.state.periodMode,
        this.state.countyFilter,
      )
    ] || null;
    const cachedUpdatedAt = this.distributionCacheUpdatedAt[
      this.distributionCacheKey(
        newScope,
        this.state.periodMode,
        this.state.countyFilter,
      )
    ] || null;
    this.setState({
      distributionScope: newScope,
      distributions: cachedDistributions,
      distributionsUpdatedAt: cachedUpdatedAt,
    }, () => {
      this.loadDistributions();
    });
  };

  handleCountyChange = (event, option) => {
    const countyFilter = option?.value || 'all';
    if (countyFilter === this.state.countyFilter) return;
    const cacheKey = this.distributionCacheKey(
      this.state.distributionScope,
      this.state.periodMode,
      countyFilter,
    );
    this.setState({
      countyFilter,
      evolution: null,
      distributions: this.distributionCache[cacheKey] || null,
      distributionsUpdatedAt: this.distributionCacheUpdatedAt[cacheKey] || null,
    }, () => {
      this.loadEvolution();
      this.loadDistributions();
    });
  };

  updateReportTopOffset = () => {
    const height = this.reportTopBar?.getBoundingClientRect().height;
    if (height > 0) {
      this.reportRef.current?.style.setProperty('--clients-topbar-height', `${Math.ceil(height)}px`);
    }
  };

  handleReportViewChange = (event, reportView) => {
    if (!reportView || reportView === this.state.reportView) return;
    this.setState({ reportView }, () => {
      this.updateReportTopOffset();
      const report = this.reportRef.current;
      if (typeof report?.scrollIntoView === 'function') {
        report.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    });
  };

  renderReportNavigation = () => (
    <Box component="nav" aria-label="Clients report sections" sx={{ display: 'flex', minWidth: 0, position: 'sticky', top: 'var(--clients-topbar-height, 64px)', zIndex: 20, py: 0, bgcolor: '#273442' }}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={this.state.reportView}
        onChange={this.handleReportViewChange}
        aria-label="Clients report view"
        sx={{
          bgcolor: '#fff',
          width: { xs: '100%', sm: 'auto' },
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
          ...reportToggleSx,
          minWidth: 0,
          maxWidth: '100%',
          display: { xs: 'grid', sm: 'inline-flex' },
          gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'none' },
          '& .MuiToggleButton-root': {
            ...reportToggleSx['& .MuiToggleButton-root'],
            minWidth: 0,
            minHeight: 30,
            height: 30,
            boxSizing: 'border-box',
            px: { xs: 0.5, sm: 1.5 },
            py: 0,
            fontSize: { xs: '0.72rem', sm: '0.8rem' },
            lineHeight: 1.25,
          },
        }}
      >
        <ToggleButton value="overview">Overview</ToggleButton>
        <ToggleButton value="activation" aria-label="Activation & repeat purchases">Activation</ToggleButton>
        <ToggleButton value="cohorts">Cohorts</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  render() {
    const {
      reportView,
      periodMode,
      countyFilter,
      distributionScope,
      countyOptions,
      evolution,
      distributions,
      recentClients,
      evolutionStatus,
      distributionsStatus,
      recentClientsStatus,
      evolutionUpdatedAt,
      distributionsUpdatedAt,
      recentUpdatedAt,
      exportStatus,
    } = this.state;
    const pageSx = {
      flexGrow: 1,
      width: { xs: 'calc(100vw - 8px)', sm: '100%' },
      maxWidth: { xs: 'calc(100vw - 8px)', sm: '100%' },
      ml: { xs: 'calc(50% - 50vw + 4px)', sm: 0 },
      // Clip horizontal overflow without creating a scroll container that
      // prevents the section tabs from sticking beneath the measured top bar.
      overflowX: 'clip',
      scrollMarginTop: 0,
      // Clients owns its initial clearance as well as the sticky offset.
      pt: 'calc(var(--clients-topbar-height, 64px) + 6px)',
      pb: { xs: 1, sm: 2, md: 3 },
      px: { xs: 0.5, sm: 1, md: 2 },
      boxSizing: 'border-box',
    };
    if (reportView === 'activation' || reportView === 'cohorts') {
      return (
        <Box ref={this.reportRef} component="main" sx={pageSx}>
          <Stack spacing={{ xs: 1, sm: 1.5 }}>
            {this.renderReportNavigation()}
            <RapClientsActivation client={this.client} reportView={reportView} />
          </Stack>
        </Box>
      );
    }
    const summary = evolution?.summary || {};
    const total = summary.total_clients || {};
    const registrations = summary.new_registrations || {};
    const active = summary.active_clients || {};
    const returning = summary.returning_clients || {};
    const periodDays = Number(evolution?.period?.days || (
      periodMode === 'custom' ? customPeriodDays(this.state.dateFrom, this.state.dateTo)
        : periodMode === 'today' || periodMode === 'yesterday'
        ? 1
        : periodMode === 'last_7d' ? 7 : periodMode === 'last_90d' ? 90 : 30
    ));
    const rangeLabel = formatPeriodRange(evolution?.period);
    const profilePeriodLabel = evolution?.period?.label || (
      periodMode === 'custom' ? 'Custom period'
        : periodMode === 'today'
        ? 'Today (in progress)'
        : periodMode === 'yesterday'
          ? 'Yesterday'
          : periodMode === 'last_7d'
            ? 'Last 7 complete days'
            : periodMode === 'last_90d'
              ? 'Last 90 complete days'
              : 'Last 30 complete days'
    );
    const hasEvolution = Boolean(evolution);
    const comparisonEnabled = hasEvolution && periodMode !== 'today';
    const selectedCounty = countyOptions.find(
      (option) => option.value === countyFilter,
    ) || { value: countyFilter, label: evolution?.filters?.county?.label || countyFilter };
    const totalDetail = hasEvolution
      ? formatTotalClientsDetail(total, periodMode)
      : '';
    const activeDetail = hasEvolution
      ? `${Number(active.share_of_total || 0).toFixed(1)}% of all clients`
      : '';
    const returningDetail = hasEvolution
      ? `${numberFormatter.format(Number(returning.value || 0))} returning purchasers`
      : '';
    const qualifyingStatuses = 'Complete, Complete with errors, or Paid';
    const activeDefinition = `Registered clients with at least one order marked ${qualifyingStatuses} during the selected period.`;
    const returningDefinition = `Clients making a second or later lifetime purchase marked ${qualifyingStatuses} during the selected ${periodDays}-day period. Their first purchase may be earlier. First-time and returning counts can overlap.`;
    const sectionStatuses = [
      evolutionStatus,
      distributionsStatus,
      recentClientsStatus,
    ];
    const refreshingAll = sectionStatuses.every((status) => status === 'idle')
      || sectionStatuses.some((status) => status === 'loading');
    const lastUpdatedAt = latestUpdate([
      evolutionUpdatedAt,
      distributionsUpdatedAt,
      recentUpdatedAt,
    ]);

    return (
      <Box
        ref={this.reportRef}
        component="main"
        sx={pageSx}
      >
        <Stack spacing={{ xs: 1, sm: 1.5 }}>
          {this.renderReportNavigation()}
          <Stack
            spacing={{ xs: 1, sm: 1.25 }}
            sx={{
              px: { xs: 1.25, sm: 1.75, md: 2 },
              py: { xs: 1.25, sm: 1.5, md: 1.75 },
              bgcolor: '#fff',
              borderRadius: { xs: '14px', sm: '16px' },
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.75, sm: 1.5 }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.35rem' }, lineHeight: 1.2, color: '#020617' }}>
                  Clients evolution
                </Typography>
                <Typography sx={{ mt: 0.25, color: '#475569', fontSize: { xs: '0.72rem', sm: '0.82rem' } }}>
                  Registrations and purchase activity
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={periodMode}
                onChange={this.handlePeriodChange}
                aria-label="Client evolution period"
                sx={{
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                  flexShrink: 0,
                  bgcolor: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
                  '& .MuiToggleButtonGroup-grouped': {
                    borderColor: 'rgba(15, 23, 42, 0.08)',
                  },
                  '& .MuiToggleButton-root': {
                    minWidth: { xs: 34, sm: 42 },
                    px: { xs: 0.75, sm: 1.25 },
                    py: { xs: 0.35, sm: 0.45 },
                    fontSize: { xs: '0.66rem', sm: '0.75rem' },
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#334155',
                    borderColor: 'rgba(15, 23, 42, 0.08)',
                    '&.Mui-selected': {
                      bgcolor: '#0f172a',
                      color: '#fff',
                      '&:hover': { bgcolor: '#111827' },
                    },
                  },
                }}
              >
                <ToggleButton value="today" aria-label="Today" title="Today (in progress)">Today</ToggleButton>
                <ToggleButton value="yesterday" aria-label="Yesterday" title="Yesterday">YDay</ToggleButton>
                <ToggleButton value="last_7d">7d</ToggleButton>
                <ToggleButton value="last_30d">30d</ToggleButton>
                <ToggleButton value="last_90d">90d</ToggleButton>
                <ToggleButton
                  ref={this.customPeriodButton}
                  value="custom"
                  aria-haspopup="dialog"
                  aria-expanded={this.state.customPeriodOpen}
                  aria-controls={this.state.customPeriodOpen ? 'clients-custom-period' : undefined}
                  onClick={periodMode === 'custom' ? (event) => this.handlePeriodChange(event, 'custom') : undefined}
                >
                  Custom
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <CustomPeriodPopover
              id="clients-custom-period"
              open={this.state.customPeriodOpen}
              anchorEl={this.customPeriodButton.current}
              onClose={() => this.setState({ customPeriodOpen: false })}
              dateFrom={this.state.draftDateFrom}
              dateTo={this.state.draftDateTo}
              onDateFromChange={(value) => this.setState({ draftDateFrom: value, customPeriodError: '' })}
              onDateToChange={(value) => this.setState({ draftDateTo: value, customPeriodError: '' })}
              onApply={this.handleCustomPeriodApply}
              error={this.state.customPeriodError}
            />

            <Stack
              direction="row"
              spacing={{ xs: 0.65, sm: 1 }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box minWidth={0} sx={{ flex: 1, alignSelf: 'flex-start', textAlign: 'left' }}>
                {rangeLabel && (
                  <Typography sx={{ color: '#64748b', fontSize: '0.74rem', fontWeight: 650 }}>
                    {rangeLabel} · {selectedCounty.label}{periodMode === 'today' ? ' · In progress' : ''}
                  </Typography>
                )}
                {lastUpdatedAt && (
                  <Typography sx={{ mt: 0.15, color: '#64748b', fontSize: '0.68rem', lineHeight: 1.35 }}>
                    Updated {formatUpdateTime(lastUpdatedAt)}
                  </Typography>
                )}
              </Box>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={this.handleRefreshAll}
                  disabled={refreshingAll}
                  aria-label="Refresh all"
                  title="Refresh all"
                  sx={{
                    minWidth: { xs: 32, sm: 'auto' },
                    px: { xs: 0.65, sm: 1 },
                    py: { xs: 0.35, sm: 0.4 },
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': {
                      ml: 0,
                      mr: { xs: 0, sm: 0.5 },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Refresh all
                  </Box>
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={exportStatus === 'loading' ? <CircularProgress size={15} /> : <DownloadRoundedIcon />}
                  endIcon={<KeyboardArrowDownRoundedIcon />}
                  disabled={!hasEvolution || !evolution?.rows?.length || exportStatus === 'loading'}
                  onClick={this.handleEvolutionExportClick}
                  aria-haspopup="menu"
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {exportStatus === 'loading' ? 'Preparing…' : 'Detailed CSV'}
                </Button>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 1.5 }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{
                p: { xs: 1, sm: 1.15 },
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '10px',
                    bgcolor: 'rgba(26, 35, 126, 0.10)',
                    color: '#1a237e',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <LocationOnRoundedIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box minWidth={0}>
                  <Typography sx={{ color: '#0f172a', fontSize: '0.78rem', fontWeight: 800 }}>
                    Client profile county
                    <ClientReportInfo label="About the county filter">Filters all client charts by current address county.</ClientReportInfo>
                  </Typography>
                </Box>
              </Stack>
              <Autocomplete
                size="small"
                disableClearable={countyFilter === 'all'}
                clearText="Reset to all counties"
                autoHighlight
                options={countyOptions}
                value={selectedCounty}
                onChange={this.handleCountyChange}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.value}>
                    <Box component="span" sx={{ flex: 1 }}>{option.label}</Box>
                    {typeof option.count !== 'undefined' && (
                      <Typography component="span" sx={{ ml: 1, color: '#64748b', fontSize: '0.72rem' }}>
                        {option.count.toLocaleString('ro-RO')} all clients
                      </Typography>
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search or select county"
                    inputProps={{ ...params.inputProps, 'aria-label': 'Client profile county' }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnRoundedIcon sx={{ color: '#64748b', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                sx={{
                  width: { xs: '100%', sm: 260, md: 290 },
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    fontSize: { xs: '16px', sm: '0.8rem' },
                    fontWeight: 650,
                  },
                  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                    fontSize: { xs: '16px', sm: '0.8rem' },
                  },
                }}
              />
            </Stack>
          </Stack>

          {evolutionStatus === 'error' && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={this.loadEvolution}>
                  Retry
                </Button>
              )}
            >
              {this.state.evolutionErrorMessage || 'Client report data could not be loaded.'}
              {hasEvolution && ' The previous result is still displayed with its original observation time.'}
            </Alert>
          )}

          <Box
            component="section"
            aria-label="Client summary metrics"
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: { xs: 0.75, sm: 1, md: 1.5 },
              alignItems: 'stretch',
              '& > *': { minWidth: 0 },
            }}
          >
            <Box sx={{ minWidth: 0, height: '100%' }}>
              <ClientMetricCard
                title="Total clients"
                value={hasEvolution ? total.value : undefined}
                detail={totalDetail}
                loading={evolutionStatus === 'loading'}
                accent="#1a237e"
              />
            </Box>
            <Box sx={{ minWidth: 0, height: '100%' }}>
              <ClientMetricCard
                title="New registrations"
                value={hasEvolution ? registrations.value : undefined}
                comparison={comparisonEnabled ? registrations.change_pct : undefined}
                comparisonLabel={comparisonDetail(registrations)}
                loading={evolutionStatus === 'loading'}
                accent="#0284c7"
              />
            </Box>
            <Box sx={{ minWidth: 0, height: '100%' }}>
              <ClientMetricCard
                title="Active clients"
                value={hasEvolution ? active.value : undefined}
                detail={activeDetail}
                comparison={comparisonEnabled ? active.change_pct : undefined}
                comparisonLabel={comparisonDetail(active)}
                definition={activeDefinition}
                loading={evolutionStatus === 'loading'}
                accent="#00897b"
              />
            </Box>
            <Box sx={{ minWidth: 0, height: '100%' }}>
              <ClientMetricCard
                title="Returning clients"
                value={hasEvolution ? Number(returning.share_of_active || 0).toFixed(1) : undefined}
                suffix="%"
                detail={returningDetail}
                comparison={comparisonEnabled ? returning.share_change_pp : undefined}
                comparisonSuffix="pp"
                comparisonLabel="vs prior period"
                definition={returningDefinition}
                loading={evolutionStatus === 'loading'}
                accent="#7c3aed"
              />
            </Box>
          </Box>

          <ClientsEvolutionChart
            response={evolution}
            status={evolutionStatus}
            onRetry={this.loadEvolution}
            onExportPeriod={this.loadEvolutionForExport}
            exportStatus={exportStatus}
            externalExportTrigger
            onExportMenuReady={this.handleEvolutionExportMenuReady}
          />


          {distributionsStatus === 'error' && Boolean(distributions) && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={() => this.loadDistributions({ force: true })}>
                  Retry
                </Button>
              )}
            >
              The previous client profile is still shown because the refresh failed.
            </Alert>
          )}

          <ClientsDistributionSection
            response={distributions}
            status={distributionsStatus}
            scope={distributionScope}
            periodLabel={profilePeriodLabel}
            countyLabel={selectedCounty.label}
            onScopeChange={this.handleScopeChange}
            onRetry={() => this.loadDistributions({ force: true })}
          />

          {recentClientsStatus === 'error' && Array.isArray(recentClients) && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={this.loadRecentClients}>
                  Retry
                </Button>
              )}
            >
              The previous recent registrations are still shown because the refresh failed.
            </Alert>
          )}

          <Box
            component="section"
            aria-label="Recent clients summary"
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                lg: 'minmax(0, 2fr) minmax(280px, 1fr)',
              },
              gap: { xs: 1, sm: 1.5 },
              alignItems: 'stretch',
              '& > *': { minWidth: 0, height: '100%' },
            }}
          >
            <Box sx={{ minWidth: 0, height: '100%' }}>
              <ClientsRecentRegistrations
                clients={recentClients}
                status={recentClientsStatus}
                onRetry={this.loadRecentClients}
              />
            </Box>
            <Box sx={{ minWidth: 0, height: '100%' }}>
              <ClientsActivationOverview
                response={evolution}
                status={evolutionStatus}
              />
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  }
}

export default RapClients;
